<template>
  <div class="task-status-wrapper">
    <div class="flex-container" :class="{ 'is-disabled': isDisabled, 'is-loading': isLoading }">
      
      <!-- 1. THE PIN (Toggle Save / Unsave Button) -->
      <!-- 🔥 CHANGED: Now uses togglePin instead of strictly handleToggle('todo') -->
      <div 
        class="pin-btn" 
        :class="{
          'state-unknown': activeStatus === 'unknown',
          'state-todo': activeStatus === 'todo',
          'state-wip': activeStatus === 'wip',
          'state-done': activeStatus === 'done'
        }"
        title="Pin / Unpin Task"
        @click="togglePin"
      >
        <!-- Tilted Push Pin SVG -->
        <svg class="tilted-pin" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
          <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z" />
        </svg>
      </div>

      <!-- 2. THE TOGGLE (Doing / Done states) -->
      <div class="doing-done-toggle">
        
        <!-- DOING -->
        <div 
          class="toggle-btn" 
          :class="{ 'is-active': activeStatus === 'wip' }"
          @click="handleToggle('wip')"
        >
          <span :title="da.updatedAt">DOING</span>
        </div>

        <!-- DONE -->
        <div 
          class="toggle-btn" 
          :class="{ 'is-active': activeStatus === 'done' }"
          @click="handleToggle('done')"
        >
          <span :title="da.createdAt">DONE</span>
        </div>
      </div>

    </div>

    <!-- Success/Error Message -->
    <transition name="fade">
      <div v-if="savedMessage" class="save-message font-bold">
        {{ savedMessage }}
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: "ThreeSwitchToggle",
  props: {
    da: {
      type: Object,
      required: true
    },
    progressStatus: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      userType: this.$store.state.AppActiveUser.userType,
      savedMessage: null,
      isLoading: false,
      localStatus: this.da.status 
    };
  },
  computed: {
    isDisabled() {
      return this.progressStatus === "complete" || this.userType !== "trainee";
    },
    activeStatus() {
      if (!this.localStatus || this.localStatus === 'unknown') {
        return 'unknown';
      }
      return this.localStatus;
    }
  },
  watch: {
    'da.status'(newStatus) {
      this.localStatus = newStatus;
    }
  },
  methods: {
    // 🔥 NEW METHOD: Checks current state before pinning/unpinning
    togglePin() {
      if (this.isDisabled || this.isLoading) return;

      if (this.activeStatus === 'todo') {
        // If it's ALREADY pinned, unpin it by setting it back to 'unknown'
        this.handleToggle('unknown');
      } else {
        // Otherwise, pin it normally
        this.handleToggle('todo');
      }
    },

    handleToggle(newStatus) {
      if (this.isDisabled || this.isLoading || this.activeStatus === newStatus) return;

      const previousStatus = this.localStatus;
      
      this.localStatus = newStatus; 
      this.saveDevelopmentAction(newStatus, previousStatus);
    },

    async saveDevelopmentAction(status, previousStatus) {
      this.isLoading = true;
      this.savedMessage = null;

      try {
        const fetchRes = await this.$store.dispatch("developmentActions/fetchDA", {
          query: {
            traineeCompetenciesId: this.$route.params.traineeCompetenciesId,
            developmentActionId: this.da.id
          }
        });

        if (fetchRes.data.length === 0) {
          await this.$store.dispatch("developmentActions/addDA", {
            developmentActionId: this.da.id,
            status,
            traineeCompetenciesId: this.$route.params.traineeCompetenciesId
          });
        } else if (fetchRes.data.length === 1) {
          await this.$store.dispatch("developmentActions/updateDA", {
            id: fetchRes.data[0].id,
            developmentActionId: this.da.id,
            status,
            traineeCompetenciesId: this.$route.params.traineeCompetenciesId
          });
        }

        this.showTemporaryMessage(status === 'unknown' ? "Unpinned!" : "Saved!");
        
        // This emits the new status to Feedback.vue, which instantly drops it back to the bottom list!
        this.$emit('status-updated', { id: this.da.id, status });

      } catch (err) {
        console.error("Could not save status", err);
        this.localStatus = previousStatus;
        this.showTemporaryMessage("Error!");
      } finally {
        this.isLoading = false;
      }
    },

    showTemporaryMessage(msg) {
      this.savedMessage = msg;
      setTimeout(() => {
        this.savedMessage = null;
      }, 2500);
    }
  }
};
</script>

<style scoped>
.task-status-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.flex-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.flex-container.is-disabled {
  opacity: 0.6;
  pointer-events: none;
  filter: grayscale(40%);
}

/* --- PIN BUTTON --- */
.pin-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  padding: 4px;
  border-radius: 50%;
}
.tilted-pin { transform: rotate(45deg); }
.pin-btn:hover { background: #f3f2f7; }

/* State 1: Unknown (Grey) */
.pin-btn.state-unknown { color: #b9b9c3; }
/* State 2: Pinned/Todo (ORG COLOR) */
.pin-btn.state-todo { color: rgb(var(--vs-primary)); transform: scale(1.15); }
/* State 3: Doing (Grey) */
.pin-btn.state-wip { color: #b9b9c3; }
/* State 4: Done (Grey) */
.pin-btn.state-done { color: #b9b9c3; }


/* --- DOING / DONE TOGGLE --- */
.doing-done-toggle {
  display: inline-flex;
  background: #f3f2f7;
  border-radius: 50px;
  height: 34px;
  padding: 3px;
  width: 150px;
}

.toggle-btn {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin: 0;
  color: #b9b9c3; /* Default Grey */
  font: 600 0.75rem/1 Montserrat, Helvetica, sans-serif;
  letter-spacing: 0.5px;
  border-radius: 50px;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  user-select: none;
}
.toggle-btn:not(.is-active):hover {
  color: #6e6b7b;
}

/* Doing/Done Active State -> ORG COLOR BG & WHITE TEXT */
.toggle-btn.is-active {
  background-color: rgb(var(--vs-primary));
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(var(--vs-primary), 0.4);
}

.save-message {
  font-size: 0.75rem;
  color: #28c76f;
  margin-right: 12px;
}
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter, .fade-leave-to { opacity: 0; }
</style>